import { useEffect, useMemo } from "react";

import { message } from "contexts";
import { useAppDispatch, useAppSelector } from "stores";
import { i18n } from "translations";

import { dialogSlice } from "./reducers";

// Register dialog name here
export type DialogFieldName =
  | "BranchDialog"
  | "ContractTypeDialog"
  | "DepartmentDialog"
  | "TitleDialog"
  | "WorkLevelDialog"
  | "AddDialogEmployee"
  | "AddDependantDialog"
  | "HistorySalaryDialog"
  | "ReasonSalaryDialog"
  | "SelectFieldDialog"
  | "MasterDataDialog"
  | "DeductionDialog"
  | "InsuranceDialog"
  | "PITRateDialog"
  | "RegionalMinimumDialog"
  | "AllowanceDialog"
  | "CompensationDialog"
  | "CompensationEarningDialog"
  | "EarningDialog"
  | "HolidayDialog"
  | "LeavePolicyDialog"
  | "LeaveTypeDialog"
  | "PayrollTemplateDialog"
  | "WorkShiftDialog"
  | "ImportTimeAttendance"
  | "ExportDialog"
  | "QuickSearchDialog"
  | "OTRateDialog"
  | "AssignTemplateDialog"
  | "AssignWorkShiftDialog"
  | "TimeOffDialog"
  | "SelectPeriodTimeAttendanceImport"
  | "LoadingRunPayroll"
  | "ViewPayslipDialog"
  | "AddBank"
  | "EmergencyContactDialog"
  | "PersonalContactDialog"
  | "BankTransferDialog"
  | "InsuranceDetailDialog"
  | "IDCardDialog"
  | "RewardDisciplineDialog"
  | "WorkAccidentDialog"
  | "ImportEmployeeDialog"
  | "SelectReportTypeDialog"
  | "ApprovalWorkflowDialog"
  | "SelectApprovalFlowDialog"
  | "SelectApproversDialog"
  | "ApprovalProgressDialog"
  | "ApprovalCommentDialog"
  | "SelectAdjustmentMethodDialog"
  | "AddAdjustmentMethodDialog"
  | "CompanySettingDialog"
  | "SelectEmployeesDialog"
  | "SelectEmployeesHeaderInfoDialog"
  | "ListEmployeesRunPayrollDialog"
  | "ImportEarningEmployeeDialog"
  | "RoleDialog"
  | "GroupEmployeeDialog"
  | "SalaryAdvanceGeneralDialog"
  | "SalaryAdvanceSelectEmployeeDialog"
  | "SalaryAdvanceDialog"
  | "SalaryAdvanceDetailDialog"
  | "SalaryPaymentGeneralDialog"
  | "SalaryPaymentSelectEmployeeDialog"
  | "SalaryPaymentDialog"
  | "SalaryPaymentDetailDialog"
  | "ListEmployeeErrorDialog"
  | "PayPeriodDialog"
  | "SyncLeaveDialog"
  | "SyncTimeAttendanceDialog"
  | "StandardWorkingDayDialog"
  | "CompanySettingDialog"
  | "AddFieldTemplateDialog"
  | "AddGroupFieldDialog"
  | "MoveFieldDialog"
  | "EmployeeEducation"
  | "PayrollCycleDialog"
  | "EmployeeTemplateDialog"
  | "GroupByDialog"
  | "AddPITDialog"
  | "GroupByViewDialog"
  | "PAMachineDialog"
  | "PAProductDialog"
  | "PAProductGroupDialog"
  | "PAReasonStopDialog"
  | "PAStandardQuantityDialog"
  | "PAMachineGroupDialog"
  | "AssignPATemplateDialog"
  | "ImportDialog"
  | "PayslipTemplateDialog"
  | "ImportTemplateFieldDialog"
  | "UpdateFieldTemplateDialog"
  | "PrintTemplateDialog"
  | "BonusPrintSampleEditorDialog"
  | "BonusPrintSampleListDialog"
  | "13ThMonthSalaryDialog"
  | "KPIBonusConfigDialog"
  | "KPISettingDialog"
  | "AddManualEmployeeDialog"
  | "EditManualEmployeeDialog"
  | "EmailTemplateDialog";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDialog = <T = any>({
  name,
  key = "id", // Key to defined data is detail or not
  onShowChange,
}: {
  name: DialogFieldName;
  key?: string;
  onShowChange?: (show?: boolean, data?: T) => void;
}) => {
  const dispatch = useAppDispatch();
  const dialog = useAppSelector((state) => state?.dialog?.[name]) || {};
  const isDetail = useMemo(
    () => Boolean(dialog?.data?.[key || ""]),
    [dialog?.data, key]
  );

  const onToggle = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: { showSuccess?: boolean; showSuccessImport?: boolean } & any
  ) => {
    if (data?.showSuccess) {
      message({
        title: i18n.t("successfully"),
        type: "Success",
      });
    }
    if (data?.showSuccessImport) {
      message({
        type: "Success",
        title: i18n.t("importSuccessfully"),
      });
    }
    dispatch(dialogSlice.actions.onToggle({ name, data: data || {} }));
  };

  const reset = () => {
    dispatch(dialogSlice.actions.reset(name));
  };

  useEffect(() => {
    if (typeof onShowChange === "function")
      onShowChange(dialog.show, dialog.data as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.show]);

  return {
    dialog: dialog as {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: T & { callback: (e?: any) => void };
      show: boolean;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: dialog?.data as T & { callback: (e?: any) => void },
    show: dialog.show,
    isDetail,
    onToggle,
    reset,
    callback: (dialog?.data?.callback as (e?: unknown) => void) || (() => {}),
  };
};
